import {
  cloneDeep,
  find,
  isEqual,
  each,
  assign,
  isFunction,
  filter,
  omit,
  findIndex,
  map,
  has,
  reduce,
} from 'lodash';

const THROTTLE_TIME_IN_MS = 100;
const THROTTLE_UNTIL_COUNT = 500;

export const createMarketBuffer = (callback) => {
  if (!isFunction(callback)) throw new Error('Callback for market buffer must be a function');

  let buffer = null;
  let timer = 0;

  const tick = () => {
    const incomingMarkets = Object.values(buffer);
    if (!incomingMarkets.length) return;
    callback(incomingMarkets);
    buffer = {};
  };

  return {
    start() {
      if (timer) clearInterval(timer);
      buffer = {};
      timer = setInterval(tick, THROTTLE_TIME_IN_MS);
    },
    stop() {
      clearInterval(timer);
      buffer = null;
    },
    append(market) {
      if (!this.active) throw new Error('Call start() before appending to market buffer');
      buffer[market.marketId] = market;
      if (Object.keys(buffer).length >= THROTTLE_UNTIL_COUNT) tick();
    },
    get active() {
      return !!buffer;
    },
  };
};

export const mergeMarkets = (markets, incomingMarkets) => {
  let eventMarkets = cloneDeep(markets);

  each(incomingMarkets, (payload) => {
    let updateEventMarkets = false;
    const foundMarket = find(eventMarkets, { marketId: payload.marketId });

    if (!foundMarket) {
      if (payload.displayStatus !== 'ON') return;
      updateEventMarkets = true;

      const newMarket = {
        marketType: payload.marketType,
        marketCode: payload.marketType.marketCode,
        marketId: payload.marketId,
        selections: payload.selections,
        source: payload.source,
        status: payload.status,
        marketSummary: payload.marketSummary,
        displayStatus: payload.displayStatus,
        offeringStatus: payload.offeringStatus,
        resultingStatus: payload.resultingStatus,
        inPlay: payload.inPlay,
      };
      eventMarkets.push(newMarket);
    } else {
      if (payload.displayStatus !== 'ON') {
        eventMarkets = filter(eventMarkets, (market) => market.marketId !== payload.marketId);
        return;
      }
      const changedMarketFields = {};
      if (foundMarket.displayStatus !== payload.displayStatus) {
        changedMarketFields.displayStatus = payload.displayStatus;
        updateEventMarkets = true;
      }
      if (foundMarket.offeringStatus !== payload.offeringStatus) {
        changedMarketFields.offeringStatus = payload.offeringStatus;
        updateEventMarkets = true;
      }
      if (foundMarket.resultingStatus !== payload.resultingStatus) {
        changedMarketFields.resultingStatus = payload.resultingStatus;
        updateEventMarkets = true;
      }
      if (foundMarket.inPlay !== payload.inPlay) {
        changedMarketFields.inPlay = payload.inPlay;
        updateEventMarkets = true;
      }
      if (!isEqual(foundMarket.marketSummary, payload.marketSummary)) {
        changedMarketFields.marketSummary = payload.marketSummary;
        updateEventMarkets = true;
      }

      if (updateEventMarkets) {
        assign(foundMarket, changedMarketFields);
      }
      each(foundMarket.selections, (selection) => {
        let updateEventMarketSelections = false;
        const foundSelection = find(
          payload.selections,
          (s) => (s.selectionType.selectionCode === selection.selectionType.selectionCode)
            && isEqual(s.selectionType.params, selection.selectionType.params),
        );
        if (!foundSelection) return;

        if (foundSelection.price.decimalValue > selection.price.decimalValue
          || foundSelection.price.decimalValue < selection.price.decimalValue
          || selection.displayStatus !== foundSelection.displayStatus
          || selection.offeringStatus !== foundSelection.offeringStatus) {
          updateEventMarketSelections = true;
        }

        if (updateEventMarketSelections) {
          let changedPrice = selection.price.changedPrice || null;
          if (foundSelection.price.decimalValue > selection.price.decimalValue) changedPrice = 'up';
          if (foundSelection.price.decimalValue < selection.price.decimalValue) changedPrice = 'down';

          const newPrice = { ...foundSelection.price, changedPrice };
          assign(selection, {
            price: newPrice,
            selectionType: foundSelection.selectionType,
            displayStatus: foundSelection.displayStatus,
            offeringStatus: foundSelection.offeringStatus,
          });
        }
      });
    }
  });

  return eventMarkets;
};

export const mergeManualResultingMarkets = (markets, incomingMarkets) => {
  const eventMarkets = cloneDeep(markets);

  each(incomingMarkets, (payload) => {
    const foundMarket = eventMarkets[payload.marketId];

    if (!foundMarket) {
      const newMarket = {
        marketType: payload.marketType,
        marketCode: payload.marketType.marketCode,
        marketId: payload.marketId,
        selections: payload.selections,
        source: payload.source,
        status: payload.status,
        marketSummary: payload.marketSummary,
        displayStatus: payload.displayStatus,
        offeringStatus: payload.offeringStatus,
        resultingStatus: payload.resultingStatus,
        inPlay: payload.inPlay,
        limitConfiguration: payload.limitConfiguration,
      };
      eventMarkets[payload.marketId] = newMarket;
    } else {
      const changedMarketFields = {};
      if (foundMarket.resultingStatus !== payload.resultingStatus) {
        changedMarketFields.resultingStatus = payload.resultingStatus;
        assign(foundMarket, changedMarketFields);
      }
      each(foundMarket.selections, (selection) => {
        const foundSelection = find(payload.selections, (s) => (s.selectionId === selection.selectionId));
        if (!foundSelection) return;

        if (foundSelection.resultingStatus !== selection.resultingStatus
          || foundSelection.outcome !== selection.outcome) {
          assign(selection, {
            ...selection,
            resultingStatus: foundSelection.resultingStatus,
            outcome: foundSelection.outcome,
          });
        }
      });
    }
  });

  return eventMarkets;
};

export const mergeTradingUiMarket = (markets, incomingMarket, multiViewList) => {
  const tradingMarkets = markets;

  let updateTradingMarkets = false;
  const foundMarket = multiViewList ? tradingMarkets[`${incomingMarket.eventId}_${incomingMarket.marketId}`] : tradingMarkets[incomingMarket.marketId];
  if (!foundMarket) {
    updateTradingMarkets = true;

    const newMarket = {
      marketType: incomingMarket.marketType,
      marketCode: incomingMarket.marketType.marketCode,
      marketId: incomingMarket.marketId,
      selections: incomingMarket.selections,
      source: incomingMarket.source,
      status: incomingMarket.status,
      marketSummary: incomingMarket.marketSummary,
      displayStatus: incomingMarket.displayStatus,
      offeringStatus: incomingMarket.offeringStatus,
      resultingStatus: incomingMarket.resultingStatus,
      inPlay: incomingMarket.inPlay,
      limitConfiguration: incomingMarket.limitConfiguration,
      gateway: incomingMarket.gateway,
    };
    return {
      market: newMarket,
      update: true,
    };
  }
  const changedMarketFields = {};
  if (foundMarket.displayStatus !== incomingMarket.displayStatus) {
    changedMarketFields.displayStatus = incomingMarket.displayStatus;
    updateTradingMarkets = true;
  }
  if (foundMarket.offeringStatus !== incomingMarket.offeringStatus) {
    changedMarketFields.offeringStatus = incomingMarket.offeringStatus;
    updateTradingMarkets = true;
  }
  if (foundMarket.resultingStatus !== incomingMarket.resultingStatus) {
    changedMarketFields.resultingStatus = incomingMarket.resultingStatus;
    updateTradingMarkets = true;
  }
  if (foundMarket.gateway !== incomingMarket.gateway) {
    changedMarketFields.gateway = incomingMarket.gateway;
    updateTradingMarkets = true;
  }
  if (!isEqual(foundMarket.marketSummary, incomingMarket.marketSummary)) {
    changedMarketFields.marketSummary = incomingMarket.marketSummary;
    updateTradingMarkets = true;
  }
  if (!isEqual(foundMarket.limitConfiguration, incomingMarket.limitConfiguration)) {
    changedMarketFields.limitConfiguration = incomingMarket.limitConfiguration;
    updateTradingMarkets = true;
  }
  if (foundMarket.inPlay !== incomingMarket.inPlay) {
    changedMarketFields.inPlay = incomingMarket.inPlay;
    updateTradingMarkets = true;
  }
  if (foundMarket.suspensioReason !== incomingMarket.suspensionReason) {
    changedMarketFields.suspensionReason = incomingMarket.suspensionReason;
    updateTradingMarkets = true;
  }

  if (updateTradingMarkets) {
    assign(foundMarket, changedMarketFields);
  }
  each(foundMarket.selections, (selection) => {
    let updateTradingMarketSelections = false;
    const foundSelection = find(
      incomingMarket.selections,
      (s) => s.selectionId === selection.selectionId,
    );
    if (!foundSelection) return;

    if (foundSelection.price.decimalValue !== selection.price.decimalValue
      || selection.price.originalFormattedValue?.value !== foundSelection.price.originalFormattedValue?.value
      || selection.price.originalFormattedValue?.signIsPlus !== foundSelection.price.originalFormattedValue?.signIsPlus
      || selection.displayStatus !== foundSelection.displayStatus
      || selection.offeringStatus !== foundSelection.offeringStatus
      || selection.price.margin !== foundSelection.price.margin
      || selection.price.bias !== foundSelection.price.bias) {
      updateTradingMarketSelections = true;
    }

    if (updateTradingMarketSelections) {
      assign(selection, {
        price: foundSelection.price,
        selectionType: foundSelection.selectionType,
        displayStatus: foundSelection.displayStatus,
        offeringStatus: foundSelection.offeringStatus,
      });
    }
    if (updateTradingMarketSelections) updateTradingMarkets = true;
  });
  return {
    market: foundMarket,
    update: updateTradingMarkets,
  };
};

export const mergeOddsCheckerMarkets = (markets, incomingMarket) => {
  const eventMarkets = cloneDeep(markets);

  let updateEventMarkets = false;
  let updateEventMarketSelections = false;
  const foundMarket = eventMarkets[`${incomingMarket.eventId}_${incomingMarket.marketId}`];
  if (!foundMarket) {
    if (incomingMarket.displayStatus !== 'ON' || (incomingMarket.marketSummary?.mainLine && !incomingMarket.marketSummary?.isMainLine)) {
      return {
        update: false,
      };
    }
    updateEventMarkets = true;

    const newMarket = {
      marketType: incomingMarket.marketType,
      marketCode: incomingMarket.marketType.marketCode,
      marketId: incomingMarket.marketId,
      eventId: incomingMarket.eventId,
      selections: incomingMarket.selections,
      source: incomingMarket.source,
      marketSummary: incomingMarket.marketSummary,
      offeringStatus: incomingMarket.offeringStatus,
      displayStatus: incomingMarket.displayStatus,
      inPlay: incomingMarket.inPlay,
      operatorId: incomingMarket.operatorId,
    };
    return {
      market: newMarket,
      update: true,
      deleteMarket: false,
    };
  }
  if (incomingMarket.displayStatus !== 'ON') {
    return {
      update: true,
      market: foundMarket,
      deleteMarket: true,
    };
  }
  const changedMarketFields = {};
  if (!isEqual(foundMarket.marketSummary, incomingMarket.marketSummary)) {
    changedMarketFields.marketSummary = incomingMarket.marketSummary;
    updateEventMarkets = true;
  }
  if (foundMarket.inPlay !== incomingMarket.inPlay) {
    changedMarketFields.inPlay = incomingMarket.inPlay;
    updateEventMarkets = true;
  }
  if (foundMarket.offeringStatus !== incomingMarket.offeringStatus) {
    changedMarketFields.offeringStatus = incomingMarket.offeringStatus;
    updateEventMarkets = true;
  }

  if (updateEventMarkets) {
    assign(foundMarket, changedMarketFields);
  }
  each(foundMarket.selections, (selection) => {
    let updateSelections = false;
    const foundSelection = find(
      incomingMarket.selections,
      (s) => s.selectionId === selection.selectionId,
    );
    if (!foundSelection) return;

    if (foundSelection.price.decimalValue !== selection.price.decimalValue) {
      updateSelections = true;
      updateEventMarketSelections = true;
    }

    if (updateSelections) {
      let changedPrice = selection.price.changedPrice || null;
      if (foundSelection.price.decimalValue > selection.price.decimalValue) changedPrice = 'up';
      if (foundSelection.price.decimalValue < selection.price.decimalValue) changedPrice = 'down';

      const newPrice = { ...foundSelection.price, changedPrice };
      assign(selection, {
        price: newPrice,
        selectionType: foundSelection.selectionType,
        displayStatus: foundSelection.displayStatus,
        offeringStatus: foundSelection.offeringStatus,
      });
    }
  });

  return {
    update: updateEventMarkets || updateEventMarketSelections,
    market: foundMarket,
    deleteMarket: false,
  };
};

// Helper for merging current game markets on socket update (params-setup/tradingUI/multiview)
export const mergeParamsSetupGameMarkets = (markets, incomingMarkets) => {
  const eventMarkets = cloneDeep(markets);

  each(incomingMarkets, (incomingMarket) => {
    const foundMarket = find(eventMarkets, { marketId: incomingMarket.marketId });

    if (!foundMarket) {
      // IF -> incoming market is not MAIN LINE market
      // THEN -> we don't want any update
      if ((incomingMarket.marketSummary?.mainLine && !incomingMarket.marketSummary?.isMainLine)) return;

      // Trying to find market that matches with one of the markets from the current list by market code and params
      const foundMarketByParamsIndex = findIndex(
        eventMarkets,
        (market) => (market.marketCode === incomingMarket.marketType.marketCode
          && isEqual(omit(market.params, 'LINE'), omit(incomingMarket.marketType.params, 'LINE'))),
      );

      // IF -> incoming market does not match with any market in the current list (by market code and params)
      // THEN -> we don't want any update
      if (foundMarketByParamsIndex === -1) return;

      // IF -> incoming market is MAIN LINE market and has the same market code and params as one of the markets from the current list
      // THEN -> we want to update the current market with the incoming market
      eventMarkets[foundMarketByParamsIndex] = {
        ...eventMarkets[foundMarketByParamsIndex],
        ...incomingMarket,
      };
      return;
    }
    // Here we know that incoming market matches with one of the markets from the current list by marketId
    const foundMarketIndex = findIndex(eventMarkets, { marketId: incomingMarket.marketId });

    // IF -> Incoming market matches with one of the markets from the current list by marketId
    // AND -> incoming market is not MAIN LINE market
    // THEN -> Delete the market prices
    if ((incomingMarket.marketSummary?.mainLine && !incomingMarket.marketSummary?.isMainLine)) {
      eventMarkets[foundMarketIndex] = {
        label: foundMarket.label,
        marketCode: foundMarket.marketCode,
        params: foundMarket.params,
      };
      return;
    }

    // IF -> Incoming market matches with one of the markets from the current list by marketId
    // AND -> incoming market is MAIN LINE market
    // THEN -> Check if the prices (probabilities) are different and update the market
    let updateEventMarketSelections = false;
    each(foundMarket.selections, (selection) => {
      const foundSelection = find(
        incomingMarket.selections,
        (s) => s.selectionId === selection.selectionId,
      );
      if (!foundSelection) return;

      if (
        foundSelection.price.probability !== selection.price.probability
        || foundSelection.price.bias !== selection.price.bias
      ) {
        updateEventMarketSelections = true;
      }
    });

    // IF -> incoming market is MAIN LINE market
    // AND -> the probabilities (or bias) are different
    // THEN -> update the market
    if (updateEventMarketSelections) {
      eventMarkets[foundMarketIndex] = {
        ...foundMarket,
        selections: incomingMarket.selections,
      };
    }
  });

  return eventMarkets;
};

// Helper for merging simulated game markets on socket update ((params-setup/tradingUI/multiview))
export const mergeParamsSetupSimulatedGameMarkets = (markets, incomingMarkets) => map(
  markets, (market) => {
    const foundMarket = find(incomingMarkets, (updatedMarket) => (
      updatedMarket?.marketType?.marketCode === market.marketCode
      && isEqual(omit(updatedMarket?.marketType?.params, 'LINE'), omit(market.params, 'LINE'))
    ));
    if (!foundMarket) return market;
    return {
      ...market,
      ...foundMarket,
    };
  },
);

// Helper for merging current game markets on event load
export const mergeCurrentGameMarketsOnEventLoad = (eventMarkets, availableMarkets) => reduce(
  availableMarkets,
  (markets, currentMarket) => {
    const foundMarket = find(
      eventMarkets,
      (market) => (market.marketCode === currentMarket.marketCode
        && isEqual(omit(market.marketType.params, 'LINE'), omit(currentMarket.params, 'LINE'))
        && (has(currentMarket.params, 'LINE') ? market.marketSummary.isMainLine : true)),
    );

    return [
      ...markets,
      {
        ...currentMarket,
        ...foundMarket || {},
      },
    ];
  },
  [],
);
